import {
  EnvConfig,
  Media,
  MediaType,
  useWorkspaceAsset,
} from '@gripp/shared-logic';
import {
  ImageGalleryImage,
  SlideAction,
  SlideLayout,
  type SlideLayoutLocationState,
  useSlideLayout,
  ViewMode,
} from '@gripp/shared-ui';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AssetFileEditForm } from './assetFileEditForm';
import EditCategory from './editCategory';
import { FilePreviewPanel } from './filesFilePreviewPanel';
import { GalleryPanel } from './filesGalleryPanel';
import { VideoPanel } from './filesVideoPanel';

type CategoryItem = { id: string; name: string };

type AssetFileEditFormPanelProps = {
  media: Media;
  assetId: string;
  allFileIds: string[];
  selectedCategories: CategoryItem[];
  onCategoriesChange: (categories: CategoryItem[]) => void;
  onCategoryEdit: () => void;
  onComplete: () => void;
};

const AssetFileEditFormPanel = ({
  media,
  assetId,
  allFileIds,
  selectedCategories,
  onCategoriesChange,
  onCategoryEdit,
  onComplete,
}: AssetFileEditFormPanelProps) => {
  const { prepareSlideTransition } = useSlideLayout();

  return (
    <AssetFileEditForm
      media={media}
      assetId={assetId}
      allFileIds={allFileIds}
      onComplete={() => {
        prepareSlideTransition(SlideAction.PREV);
        onComplete();
      }}
      onCategoryEdit={onCategoryEdit}
      selectedCategories={selectedCategories}
      onCategoriesChange={onCategoriesChange}
    />
  );
};

export const AssetFileEdit = () => {
  const { assetId = '', mediaId = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as SlideLayoutLocationState | null;
  const initialTransition =
    locationState?.slideLayoutInitialTransition === SlideAction.NEXT ||
    locationState?.slideLayoutInitialTransition === SlideAction.PREV
      ? locationState.slideLayoutInitialTransition
      : undefined;
  const { workspaceAsset } = useWorkspaceAsset(assetId);

  const allFiles: Media[] = useMemo(
    () => workspaceAsset?.asset?.media || [],
    [workspaceAsset?.asset?.media]
  );

  const selectedMedia = useMemo(
    () => allFiles.find((m) => m.id === mediaId) ?? null,
    [allFiles, mediaId]
  );

  const imageFiles = useMemo(
    () => allFiles.filter((m) => m.type === MediaType.Image),
    [allFiles]
  );

  const galleryImages: ImageGalleryImage[] = useMemo(
    () =>
      imageFiles.map((m) => ({
        id: m.id,
        url: `${EnvConfig.mediaUrl}/images/${m.id}/original.${m.extension}`,
        name: m.name,
      })),
    [imageFiles]
  );

  const galleryInitialIndex = useMemo(() => {
    const idx = galleryImages.findIndex((img) => img.id === mediaId);
    return idx >= 0 ? idx : 0;
  }, [galleryImages, mediaId]);

  const [selectedCategories, setSelectedCategories] = useState<CategoryItem[]>(
    []
  );

  // Initialize from media when it loads
  useEffect(() => {
    if (selectedMedia?.categories) {
      setSelectedCategories(selectedMedia.categories as CategoryItem[]);
    }
  }, [selectedMedia?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [isCategoryPanelOpen, setIsCategoryPanelOpen] = useState(false);

  if (!assetId || !selectedMedia) return null;

  const isImage = selectedMedia.type === MediaType.Image;
  const isVideo = selectedMedia.type === MediaType.Video;

  const backToAssetCard = () =>
    navigate('../../..', {
      relative: 'path',
      state: { slideLayoutInitialTransition: SlideAction.PREV },
    });

  const backToPreview = () =>
    navigate('..', {
      relative: 'path',
      state: {
        source: 'assetCard',
        slideLayoutInitialTransition: SlideAction.PREV,
      },
    });

  const leftColumn = {
    title:
      selectedMedia.name && selectedMedia.name.length > 30
        ? `${selectedMedia.name.substring(0, 27)}...`
        : selectedMedia.name ?? 'File',
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent' as const,
    backButtonTransitionExitColumn: 'both' as const,
    onBackButtonPress: backToAssetCard,
    disableScroll: true,
    mainComponent: isImage ? (
      <GalleryPanel
        images={galleryImages}
        initialIndex={galleryInitialIndex}
        disableImageChange
      />
    ) : isVideo ? (
      <VideoPanel media={selectedMedia} />
    ) : (
      <FilePreviewPanel media={selectedMedia} />
    ),
  };

  const rightColumn = {
    title: 'topNav.edit',
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent' as const,
    onBackButtonPress: backToPreview,
    disableScroll: true,
    mainComponent: (
      <AssetFileEditFormPanel
        media={selectedMedia}
        assetId={assetId}
        allFileIds={allFiles.map((m) => m.id)}
        onComplete={backToPreview}
        onCategoryEdit={() => setIsCategoryPanelOpen((prev) => !prev)}
        selectedCategories={selectedCategories}
        onCategoriesChange={setSelectedCategories}
      />
    ),
  };

  const thirdColumn = isCategoryPanelOpen
    ? {
        title: 'file.create.category',
        showBackButton: true,
        disableScroll: true,
        onBackButtonPress: () => setIsCategoryPanelOpen(false),
        mainComponent: (
          <EditCategory
            selectedCategories={selectedCategories}
            onCategoriesChange={setSelectedCategories}
          />
        ),
      }
    : undefined;

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      thirdColumn={thirdColumn}
      viewMode={ViewMode.DUAL}
      initialTransition={initialTransition}
    />
  );
};
